import type { ProficiencyLevel } from '@/types/performance-profile';
import type { IconProps } from '@amelio/ui-library';

import { useGetProficiencyLevels } from '@/api/performance-profile';
import { resolveProficiencyLevelColor } from '@/components/ProficiencyIcon/resolveProficiencyLevelColor';
import { getLocalizedText } from '@/utils/localization';
import { orderProficiencyLevelsWorstFirst } from '@/utils/proficiencyLevel';

export type SelectableProficiencyLevel = ProficiencyLevel;

interface ProficiencyLevelPresentation {
    label: Record<string, string>;
    icon: string;
    iconUrl?: string;
    color: string;
}

export const useProficiencyLevelPresentation = () => {
    const { data: proficiencyLevels, isLoading, isError } = useGetProficiencyLevels();

    // The API (`/proficiencylevel`) returns levels pre-sorted by SortOrder, then CreatedAt, in
    // best-to-worst order.
    // Map proficiency levels by their ID (from API) for easy lookup
    const proficiencyLevelMap = (proficiencyLevels ?? []).reduce(
        (acc, level, index) => ({
            ...acc,
            [level.id]: {
                label: level.label,
                icon: level.icon,
                iconUrl: level.iconUrl,
                color: resolveProficiencyLevelColor({
                    iconColor: level.iconColor,
                    levelIndex: index,
                }),
            },
        }),
        {} as Record<string, ProficiencyLevelPresentation>,
    );

    const displayLevels = orderProficiencyLevelsWorstFirst(proficiencyLevels ?? []).map(
        (level) => level.id,
    );

    // Ranking order, the API order: best level first. Separate from `displayLevels`, which is the
    // reading order; a level's rank must not move when the scale is read the other way round.
    const orderedLevels = (proficiencyLevels ?? []).map((level) => level.id);

    const getProficiencyLabel = (levelId: string, fallbackNaLabel: string): string => {
        if (levelId === 'na') return fallbackNaLabel;
        const profData = proficiencyLevelMap[levelId];
        return profData ? getLocalizedText(profData.label) || levelId : levelId;
    };

    const getProficiencyIcon = (
        levelId: string,
    ): { name: IconProps['name']; color: string } | null => {
        if (levelId === 'na') return null;
        const profData = proficiencyLevelMap[levelId];
        return profData
            ? { name: profData.iconUrl as IconProps['name'], color: profData.color }
            : null;
    };

    return {
        isLoading,
        // A failed load is not the same as an empty framework. Consumers need to tell
        // "we could not ask" from "there are none", so they do not offer an action that
        // cannot succeed or state an absence they never observed (#15036).
        isError,
        proficiencyLevelMap,
        displayLevels,
        orderedLevels,
        getProficiencyLabel,
        getProficiencyIcon,
    };
};
